import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import {
  Table, Input, message, Space, DatePicker, Button, Popconfirm,
  Typography, Tag, Select, Row, Col, Pagination, Empty, Spin,
  Tabs, Badge, Card, Statistic, Tooltip, Modal, Divider, Alert,
  Dropdown, Menu, Segmented, Avatar, Popover
} from 'antd';
const { Title, Text } = Typography;
import {
  SearchOutlined, PhoneOutlined, EyeOutlined, DeleteOutlined, DeleteFilled, DollarCircleOutlined,
  ReloadOutlined, UserOutlined, MailOutlined, DatabaseOutlined, ClearOutlined,
  AppstoreOutlined, TrophyOutlined, StarOutlined, TagOutlined,
  FilterOutlined, DownloadOutlined, PlusOutlined, SettingOutlined,
  LineChartOutlined, TeamOutlined, CalendarOutlined, ExportOutlined,
  MoreOutlined, SyncOutlined, CheckCircleOutlined, WarningOutlined,
  CloudOutlined, ClockCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, FileExcelOutlined,
  GlobalOutlined, CloseOutlined
} from '@ant-design/icons';
import SocketManager from '../../../SocketManager';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import API from '../../API/API';
import { useNavigate, useLocation } from 'react-router-dom';
import Config from '../../API/Config';
import AddLead from "./Addlead";
const { RangePicker } = DatePicker;
import RecycleBin from './RecycleBin';
import LeadDownload from '../../Excel/LeadDownload';
import { ROUTES } from '../../Routes/Routes';
import LeadCard from './LeadCard';
import EnhancedLeadSearch from '../../Components/EnhancedLeadSearch/EnhancedLeadSearch';
import SearchFieldsSelect, { useSearchFields, getSearchFieldsPlaceholder } from '../../Components/SearchFieldsSelect/SearchFieldsSelect';
import { useDomainGroups } from '../../Context/DomainGroupsContext';
import { isSuperAdminUser } from '../../utils/roles';

dayjs.extend(relativeTime);

const MY_LEADS_STATE_KEY = 'my-leads:list-state';
// The "Search in" choice, kept per browser so it stays the same between visits.
const MY_LEADS_SEARCH_FIELDS_KEY = 'my-leads:search-fields';
const MY_LEADS_SCROLL_KEY = 'my-leads:scroll-top';
const MY_LEADS_RESTORE_KEY = 'my-leads:should-restore';

/**
 * The lane a lead was assigned from is resolved by the backend and arrives on
 * the lead as tierLane (the domain group key) and tierLaneLabel (its name).
 * Both come from the DomainGroup collection, so a group an admin renames or
 * adds is reflected here without this file knowing any lane by name.
 */

const getInitialRestoreFlag = (locationState) => {
  try {
    return (
      locationState?.restoreMyLeads === true ||
      sessionStorage.getItem(MY_LEADS_RESTORE_KEY) === 'true'
    );
  } catch (error) {
    console.error('Failed to read MyLeads restore marker', error);
    return locationState?.restoreMyLeads === true;
  }
};

/**
 * Returns a function whose identity never changes but which always runs the
 * latest `callback`. Passing these to the memoized LeadCard keeps the cards
 * from re-rendering just because this page rendered again.
 */
const useStableCallback = (callback) => {
  const callbackRef = useRef(callback);

  useLayoutEffect(() => {
    callbackRef.current = callback;
  });

  return useCallback((...args) => callbackRef.current(...args), []);
};

const MyLeads = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // The TIER-1 lanes an admin can filter by, straight from Domain Mapping.
  const { groupsForTier } = useDomainGroups();
  const socketManagerRef = useRef(null);
  const pendingScrollRestoreRef = useRef(null);
  const shouldRestoreMyLeadsRef = useRef(getInitialRestoreFlag(location.state));
  const skipActiveViewRestoreSyncRef = useRef(false);
  const fetchControllerRef = useRef(null);

  // Get today's date range
  const getTodayDateRange = () => {
    return [dayjs().startOf('day'), dayjs().endOf('day')];
  };

  // Date presets for better UX
  const datePresets = [
    { label: 'Today', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
    { label: 'Yesterday', value: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')] },
    { label: 'Last 7 Days', value: [dayjs().subtract(7, 'day').startOf('day'), dayjs().endOf('day')] },
    { label: 'Last 30 Days', value: [dayjs().subtract(30, 'day').startOf('day'), dayjs().endOf('day')] },
    { label: 'This Month', value: [dayjs().startOf('month'), dayjs().endOf('day')] },
    { label: 'Last Month', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  ];

  // Initialize with TODAY as default date range
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  // Bumped each time a server answer replaces the list, so the cards are
  // rebuilt from the new results, as they were when the list was swapped
  // for a spinner.
  const [resultsVersion, setResultsVersion] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFields, setSearchFields] = useSearchFields(MY_LEADS_SEARCH_FIELDS_KEY);

  const handleSearchFieldsChange = (value) => {
    setSearchFields(value);
    // A new group gives new results, so a search starts again from page 1.
    if (searchTerm?.trim()) setCurrentPage(1);
  };
  const [dateRange, setDateRange] = useState(getTodayDateRange());
  const [loginAgent, setLoginAgent] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [recycleBinVisible, setRecycleBinVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedLeadStage, setSelectedLeadStage] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [activeView, setActiveView] = useState('all');
  const [socketStatus, setSocketStatus] = useState({
    connected: false,
    reconnectAttempts: 0,
    lastUpdate: null
  });
  const [stats, setStats] = useState({
    total: 0,
    newLeads: 0,
    quoted: 0,
    converted: 0
  });
const [selectedAssignment, setSelectedAssignment] = useState(null);
const [selectedLeadType, setSelectedLeadType] = useState(null);
const [selectedDomain, setSelectedDomain] = useState(null);
const [selectedTierLane, setSelectedTierLane] = useState('all');
const [phoneNumberFormat, setPhoneNumberFormat] = useState(null);
const [timezoneDecision, setTimezoneDecision] = useState(null);
// Admin only: 'default' keeps the existing date logic; 'createdAt' filters by lead created date.
const [dateBasis, setDateBasis] = useState('default');
  const [isMyLeadsStateReady, setIsMyLeadsStateReady] = useState(false);
  const shouldRestoreMyLeads = shouldRestoreMyLeadsRef.current;

  const persistMyLeadsState = () => {
    try {
      sessionStorage.setItem(MY_LEADS_STATE_KEY, JSON.stringify({
        searchTerm,
        dateRange:
          Array.isArray(dateRange) && dateRange[0] && dateRange[1]
            ? [dateRange[0].toISOString(), dateRange[1].toISOString()]
            : null,
        currentPage,
        pageSize,
        selectedLeadStage,
        selectedAgentId,
        activeView,
        selectedAssignment,
        selectedLeadType,
        selectedDomain,
        selectedTierLane,
        phoneNumberFormat,
        timezoneDecision,
        dateBasis,
      }));
    } catch (error) {
      console.error('Failed to persist MyLeads state', error);
    }
  };

  const handleBeforeNavigateToLeadDetails = () => {
    persistMyLeadsState();
  };

const domains = [
  'advanceusedautoparts.com',
  'auto.advanceusedautoparts.com',
  'auto.premiumusedautoparts.com',
  'auto.premiumusedengine.com',
  'auto.premiumusedtransmission.com',
  'inbound call',
  'parts.premiumusedautoparts.com',
  'premiumusedabsmodule.com',
  'premiumusedairbag.com',
  'premiumusedautoparts.com',
  'premiumuseddriveshaft.com',
  'premiumusedengine.com',
  'premiumusedinstrumentcluster.com',
  'premiumusedsteeringcolumn.com',
  'premiumusedtransfercase.com',
  'premiumusedtransmission.com'
];
  const leadStages = [
    "New", "Call Back", "Voice Mail", "Follow-Up", "Quoted",
    "Pending Payment", "Payment Completed", "Sales Made",
    "Part Not Available", "Wrong Number", "Not Interested",
    "Hang Up", "Fake", "Test Lead", "Negotiation",
  ];

  const getStageColor = (stage) => {
    const colors = {
      "New": "blue",
      "Call Back": "cyan",
      "Voice Mail": "purple",
      "Follow-Up": "orange",
      "Quoted": "gold",
      "Pending Payment": "volcano",
      "Payment Completed": "green",
      "Sales Made": "success",
      "Part Not Available": "red",
      "Wrong Number": "default",
      "Not Interested": "magenta",
      "Hang Up": "error",
      "Fake": "warning",
      "Test Lead": "processing",
      "Negotiation": "lime",
    };
    return colors[stage] || "default";
  };

  useEffect(() => {
    const UD = localStorage.getItem('userData');
    if (!UD) {
      navigate(ROUTES?.LOGIN);
      return;
    }

    const parsedUser = JSON.parse(UD);
    setLoginAgent(parsedUser);

    if (parsedUser?.role === "SALES-AGENT") {
      setActiveView("my");
    } else {
      setActiveView("all");
    }
  }, [navigate]);
useEffect(() => {
  if (!shouldRestoreMyLeads) {
    setIsMyLeadsStateReady(true);
    return;
  }

  try {
    const savedState = sessionStorage.getItem(MY_LEADS_STATE_KEY);
    const savedScrollTop = sessionStorage.getItem(MY_LEADS_SCROLL_KEY);

    if (savedState) {
      const parsedState = JSON.parse(savedState);

      setSearchTerm(parsedState?.searchTerm ?? "");
      setCurrentPage(parsedState?.currentPage ?? 1);
      setPageSize(parsedState?.pageSize ?? 24);
      setSelectedLeadStage(parsedState?.selectedLeadStage ?? null);
      setSelectedAgentId(parsedState?.selectedAgentId ?? null);
      skipActiveViewRestoreSyncRef.current = true;
      setActiveView(parsedState?.activeView ?? 'all');
      setSelectedAssignment(parsedState?.selectedAssignment ?? null);
      setSelectedLeadType(parsedState?.selectedLeadType ?? null);
      setSelectedDomain(parsedState?.selectedDomain ?? null);
      setSelectedTierLane(parsedState?.selectedTierLane ?? 'all');
      setPhoneNumberFormat(parsedState?.phoneNumberFormat ?? null);
      setTimezoneDecision(parsedState?.timezoneDecision ?? null);
      setDateBasis(parsedState?.dateBasis === 'createdAt' ? 'createdAt' : 'default');

      if (
        Array.isArray(parsedState?.dateRange) &&
        parsedState.dateRange.length === 2 &&
        parsedState.dateRange[0] &&
        parsedState.dateRange[1]
      ) {
        setDateRange([
          dayjs(parsedState.dateRange[0]),
          dayjs(parsedState.dateRange[1]),
        ]);
      }
    }

    pendingScrollRestoreRef.current = savedScrollTop ? Number(savedScrollTop) : null;
  } catch (error) {
    console.error('Failed to restore MyLeads state', error);
    pendingScrollRestoreRef.current = null;
  } finally {
    setIsMyLeadsStateReady(true);
  }
}, [shouldRestoreMyLeads]);
useEffect(() => {
  if (skipActiveViewRestoreSyncRef.current) {
    skipActiveViewRestoreSyncRef.current = false;
    return;
  }

  if (activeView === 'totalpaid') {
    setSelectedLeadStage('Payment Completed');
  } else if (activeView === 'advance') {
    setSelectedLeadStage('Pending Payment');
  } else if (activeView === 'all' || activeView === 'unassigned' || activeView.startsWith('TIER-')) {
    // For all, unassigned, and tier views, clear the stage filter
    setSelectedLeadStage(null);
  }
}, [activeView]);

useEffect(() => {
  if (activeView !== 'TIER-1' && selectedTierLane !== 'all') {
    setSelectedTierLane('all');
  }
}, [activeView, selectedTierLane]);

  useEffect(() => {
    const fetchAgents = async () => {
      if (loginAgent?.role !== "ADMIN") return;

      try {
        const response = await API.userGetAll();
        if (response?.success) {
          const salesAgents = (response.data || []).filter(user => user.role === "SALES-AGENT");
          setAgents(salesAgents);
        }
      } catch (error) {
        console.error("Failed to fetch agents", error);
      }
    };

    fetchAgents();
  }, [loginAgent]);

  const calculateStats = (leadsData) => {
    // For SALES-AGENT, filter leads that belong to them
    let filteredLeads = leadsData;

    if (loginAgent?.role === "SALES-AGENT") {
      filteredLeads = leadsData.filter(lead => {
        // Check both possible agent ID fields
        return lead.handledBy?._id === loginAgent.id ||
          lead.agentId === loginAgent.id ||
          (lead.agentId && lead.agentId._id === loginAgent.id);
      });
    }

    const newLeads = filteredLeads.filter(lead => lead.leadStage === "New").length;
    const quoted = filteredLeads.filter(lead => lead.leadStage === "Quoted").length;
    const converted = filteredLeads.filter(lead => lead.leadStage === "Sales Made").length;

    setStats({
      total: filteredLeads.length,
      newLeads,
      quoted,
      converted
    });
  };

const fetchLeads = async () => {
    // A newer request replaces one still in flight: the older one is
    // cancelled, and its response is ignored if it arrives anyway.
    fetchControllerRef.current?.abort();
    const controller = new AbortController();
    fetchControllerRef.current = controller;

    setLoading(true);

    try {
        const userData = JSON.parse(localStorage.getItem("userData"));

        if (!userData?.id) {
            message.error("Authentication issue — please login again");
            return;
        }

        let startDateStr = '';
        let endDateStr = '';

        const isSearching = searchTerm?.trim().length > 0;

        if (!isSearching) {
            if (Array.isArray(dateRange) && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
                startDateStr = dateRange[0].format('YYYY-MM-DD');
                endDateStr = dateRange[1].format('YYYY-MM-DD');
                                console.log("📅 Date Range:", { startDateStr, endDateStr });

            } else {
                const todayRange = getTodayDateRange();
                startDateStr = todayRange[0].format('YYYY-MM-DD');
                endDateStr = todayRange[1].format('YYYY-MM-DD');
                setDateRange(todayRange);
            }
        }

        const params = {
            page: currentPage,
            limit: pageSize,
        };

        if (loginAgent?.role === "ADMIN") {
            if (phoneNumberFormat) {
                params.phoneNumberFormat = phoneNumberFormat;
            }

            if (timezoneDecision) {
                params.timezoneDecision = timezoneDecision;
            }
        }

        if (isSearching) {
            params.search = searchTerm.trim();
            // Lets the server skip its separate count when this page already shows the total.
            params.searchSource = 'my-leads';
            params.searchFields = searchFields;
        } else {
            const isAdminAssignedView =
                loginAgent.role === "ADMIN" &&
                (activeView === 'all' || activeView.startsWith("TIER-"));
            const isSalesAgentMyView =
                loginAgent.role === "SALES-AGENT" &&
                activeView === 'my';
            const effectiveLeadStatus = activeView === 'unassigned'
                ? 'unassigned'
                : (selectedAssignment || ((isAdminAssignedView || isSalesAgentMyView) ? 'assigned' : null));

            const useCreatedDateBasis = loginAgent?.role === "ADMIN" && dateBasis === 'createdAt';

            if (useCreatedDateBasis) {
                // Admin "Created Date" option: date range applies to createdAt only;
                // a selected stage filters on the lead's current stage.
                params.dateField = 'createdAt';
                params.startDate = startDateStr;
                params.endDate = endDateStr;
                if (selectedLeadStage) {
                    params.status = selectedLeadStage;
                }
            }
            // Check if we have a stage filter selected
            else if (selectedLeadStage) {
                // New-stage results are based on when the lead was assigned.
                if (selectedLeadStage === "New") {
                    params.dateField = 'assignedDate';
                    params.startDate = startDateStr;
                    params.endDate = endDateStr;
                    params.status = "New";
                } 
                // For all other stages, use leadStageHistory with the specific stage
                else {
                       console.log("🎯 Applying stage filter:", {
                        stage: selectedLeadStage,
                        startDate: startDateStr,
                        endDate: endDateStr
                    });
                    
                    // Use the stage history filter
                    params.stageHistoryStage = selectedLeadStage;
                    params.stageHistoryDate = startDateStr;
                         params.stageHistoryEndDate=endDateStr;
                    params.status = selectedLeadStage;
                    
              
   console.log("✅ Stage params:", {
                        stageHistoryStage: params.stageHistoryStage,
                        stageHistoryDate: params.stageHistoryDate,
                        stageHistoryEndDate: params.stageHistoryEndDate,
                        status: params.status
                    });
                }
            } else {
                // No stage filter - use assignedDate for assigned views, createdAt otherwise
                params.dateField = effectiveLeadStatus === 'assigned' ? 'assignedDate' : 'createdAt';
                params.startDate = startDateStr;
                params.endDate = endDateStr;
            }

            // Add other filters (same as before)
            if (selectedLeadType) {
                params.type = selectedLeadType;
            }
            
            if (effectiveLeadStatus) {
                params.leadStatus = effectiveLeadStatus;
            }
            
            if (selectedDomain) {
                params.source = selectedDomain;
            }
            
            if (loginAgent.role === "ADMIN" && activeView.startsWith("TIER-")) {
                params.tierType = activeView;
                if (activeView === "TIER-1" && selectedTierLane !== 'all') {
                    params.tierLane = selectedTierLane;
                }
            }

            // Agent filter
            if (loginAgent.role === "SALES-AGENT") {
                params.agentId = userData.id;
                params.agentMode = "include";
            } else if (loginAgent.role === "ADMIN" && selectedAgentId) {
                params.agentId = selectedAgentId;
            }
        }

  console.log("🚀 FINAL PARAMS:", params);        
        // The server sends only the fields the My Leads list uses
        // (LeadController LEAD_VIEW_FIELDS['my-leads']).
        params.view = 'my-leads';

        const response = await API.leadGetAll(params, { signal: controller.signal });

        if (controller.signal.aborted) return;
        setResultsVersion((version) => version + 1);

      if (
        response &&
        (response.status === true ||
          response.success === true ||
          response.statusCode === 200 ||
          Array.isArray(response.data))
      ) {
        const leadsData = response.data || response.leads || [];
        const total = response.pagination?.totalRecords || response.total || leadsData.length || 0;

        setLeads(leadsData);
        setTotalRecords(total);
        calculateStats(leadsData);

        if (isSearching && leadsData.length > 0) {
          message.success({
            content: `Found ${leadsData.length} lead(s) across entire database`,
            duration: 2,
            icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
          });
        }
      } else {
        message.warning(response?.message || "No leads found");
        setLeads([]);
        setTotalRecords(0);
        calculateStats([]);
      }

    } catch (error) {
      if (controller.signal.aborted) return;
      setResultsVersion((version) => version + 1);

      console.error("[fetchLeads] Failed:", error);
      message.error("Failed to load leads");
      setLeads([]);
      setTotalRecords(0);
      calculateStats([]);
    } finally {
      // Only the newest request turns the spinner off.
      if (fetchControllerRef.current === controller) {
        setLoading(false);
      }
    }
};

const displayedLeads = leads.filter(lead => {
  if (loginAgent?.role !== "ADMIN") {
    return true;
  }

  if (activeView.startsWith("TIER-")) {
    if (lead.tierType !== activeView) {
      return false;
    }

    if (activeView === 'TIER-1' && selectedTierLane !== 'all') {
      return lead.tierLane === selectedTierLane;
    }

    return true;
  }

  return true;
});

  useEffect(() => {
    if (leads.length > 0) {
      calculateStats(leads);
    }
  }, [loginAgent, leads]);

useEffect(() => {
  if (!isMyLeadsStateReady) return;

  persistMyLeadsState();
}, [isMyLeadsStateReady, searchTerm, dateRange, currentPage, pageSize, selectedLeadStage, selectedAgentId, activeView, selectedAssignment, selectedLeadType, selectedDomain, selectedTierLane, phoneNumberFormat, timezoneDecision, dateBasis]);

// The request sends the search text trimmed, so a change that only adds or
// removes surrounding spaces is not a new request.
const effectiveSearchTerm = searchTerm?.trim() ?? '';
// The "Search in" group only changes the results of a search, so changing it
// with an empty search box sends no request.
const effectiveSearchFields = effectiveSearchTerm ? searchFields : null;

// When the list is requested:
// - A change to the search text waits until typing pauses for 0.5 s.
// - Opening the page, and page, page size, view, filter and date changes,
//   request it at once.
// The search text of the last request sent or waiting; null before the first.
const requestedSearchTermRef = useRef(null);
// After the page opens with its saved filters, and after a view change, the
// effects above adjust the stage filter and the tier lane one render later.
// The request waits for that render so it goes out once, with those filters.
// The view of the last request; null before the first.
const requestedViewRef = useRef(null);
const [viewSettleTick, setViewSettleTick] = useState(0);

useEffect(() => {
  if (!isMyLeadsStateReady) return;

  if (activeView !== requestedViewRef.current) {
    requestedViewRef.current = activeView;
    setViewSettleTick((tick) => tick + 1);
    return;
  }

  const waitForTyping = requestedSearchTermRef.current !== null
    && effectiveSearchTerm !== requestedSearchTermRef.current;
  requestedSearchTermRef.current = effectiveSearchTerm;

  if (!waitForTyping) {
    fetchLeads();
    return;
  }

  const delaySearch = setTimeout(() => {
    fetchLeads();
  }, 500);

  return () => clearTimeout(delaySearch);
}, [isMyLeadsStateReady, viewSettleTick, currentPage, pageSize, dateRange, effectiveSearchTerm, effectiveSearchFields, selectedLeadStage, selectedAgentId, selectedAssignment, selectedLeadType, activeView, loginAgent?.role, selectedDomain, selectedTierLane, phoneNumberFormat, timezoneDecision, dateBasis]);

// Cancel a request still in flight when the page closes.
useEffect(() => () => fetchControllerRef.current?.abort(), []);

useEffect(() => {
  if (!isMyLeadsStateReady || !shouldRestoreMyLeads || loading) return;

  const scrollTop = pendingScrollRestoreRef.current;
  pendingScrollRestoreRef.current = null;

  try {
    sessionStorage.removeItem(MY_LEADS_RESTORE_KEY);
  } catch (error) {
    console.error('Failed to clear MyLeads restore marker', error);
  }

  requestAnimationFrame(() => {
    if (scrollTop == null) return;

    const scrollContainer = document.querySelector('.ant-layout-content');

    if (scrollContainer) {
      scrollContainer.scrollTo({ top: scrollTop, behavior: 'auto' });
    } else {
      window.scrollTo({ top: scrollTop, behavior: 'auto' });
    }
  });
}, [isMyLeadsStateReady, shouldRestoreMyLeads, loading, leads.length]);
  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    // The list stays on screen while the next page loads, so go back to the
    // top here; the list collapsing into a spinner used to do that.
    document.querySelector('.ant-layout-content')?.scrollTo({ top: 0, behavior: 'auto' });
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedLeadStage(null);
    setSelectedAgentId(null);
    setSelectedDomain(null); // Add this line
    setSelectedAssignment(null);
    setSelectedLeadType(null);
    setSelectedTierLane('all');
    setPhoneNumberFormat(null);
    setDateRange(getTodayDateRange()); // Reset to TODAY as default
    setCurrentPage(1);
    message.info({
      content: "All filters cleared - showing today's data",
      icon: <ClearOutlined />
    });
  };

  const resetDateFilter = () => {
    setDateRange(getTodayDateRange()); // Reset to TODAY
    message.info({
      content: "Date filter reset to today",
      icon: <CalendarOutlined />
    });
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    const currentUserId = userData?.id;

    if (!currentUserId) return;

    socketManagerRef.current = new SocketManager(Config.baseURL);
    const sm = socketManagerRef.current;

    sm.on('socket-connected', (data) => {
      setSocketStatus({
        connected: true,
        reconnectAttempts: 0,
        lastUpdate: new Date()
      });
      sm.joinRoom(`agent_${currentUserId}`);
      message.success({
        content: "Real-time updates connected",
        icon: <CloudOutlined style={{ color: '#52c41a' }} />
      });
    });

    sm.on('socket-disconnected', (data) => {
      setSocketStatus(prev => ({ ...prev, connected: false }));
      message.warning("Real-time updates disconnected");
    });

    const handleLeadAssigned = (newLead) => {
      if (String(newLead.agentId) === String(currentUserId)) {
        setLeads(prevLeads => {
          const leadExists = prevLeads.some(lead => lead._id === newLead.lead._id);
          if (!leadExists) {
            return [newLead.lead, ...prevLeads];
          }
          return prevLeads;
        });
        setTotalRecords(prev => prev + 1);
        setStats(prev => ({ ...prev, total: prev.total + 1, newLeads: prev.newLeads + 1 }));
        message.success({
          content: `New lead assigned: ${newLead.lead.personalInfo?.fullName}`,
          icon: <UserOutlined style={{ color: '#1890ff' }} />
        });
      }
    };

    sm.on('lead-assigned', handleLeadAssigned);

    return () => {
      if (socketManagerRef.current) {
        socketManagerRef.current.disconnect();
        socketManagerRef.current = null;
      }
    };
  }, []);

  const deleteSelected = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('No rows selected.');
      return;
    }

    Modal.confirm({
      title: 'Delete Selected Records?',
      content: `Are you sure you want to move ${selectedRowKeys.length} record(s) to Recycle Bin?`,
      icon: <WarningOutlined style={{ color: '#ff4d4f' }} />,
      okText: 'Yes, Delete',
      cancelText: 'Cancel',
      okType: 'danger',
      centered: true,
      onOk: async () => {
        try {
          const response = await API.DeleteLeadSoft(selectedRowKeys);

          if (response.status) {
            setLeads((prev) => prev.filter((item) => !selectedRowKeys.includes(item._id)));
            setSelectedRowKeys([]);
            setStats(prev => ({ ...prev, total: prev.total - selectedRowKeys.length }));
            message.success(`${selectedRowKeys.length} record(s) moved to Recycle Bin`);
          } else {
            message.error(response.message || 'Failed to delete selected records.');
          }
        } catch (error) {
          console.error('Error deleting records:', error);
          message.error('Failed to delete selected records.');
        }
      }
    });
  };

  const handleSingleDelete = async (leadId) => {
    try {
      const response = await API.DeleteLeadSoft([leadId]);

      if (response.status) {
        setLeads((prev) => prev.filter((lead) => lead._id !== leadId));
        setTotalRecords((prev) => prev - 1);
        setStats(prev => ({ ...prev, total: prev.total - 1 }));
        message.success({
          content: "Lead moved to Recycle Bin",
          icon: <DeleteOutlined />
        });
      } else {
        message.error(response.message || "Failed to delete lead");
      }
    } catch (error) {
      console.error("Delete error:", error);
      message.error("Failed to delete lead");
    }
  };

  // LeadCard is memoized. These keep one identity across renders and always
  // run the latest version, so typing in the search box doesn't re-render
  // every card.
  const handleCardDelete = useStableCallback(handleSingleDelete);
  const handleCardRefresh = useStableCallback(fetchLeads);
  const handleCardBeforeNavigate = useStableCallback(handleBeforeNavigateToLeadDetails);

  const isSearching = searchTerm?.trim().length > 0;

  const handlePhoneValidationFilterChange = (setFilter) => (value) => {
    setFilter(value || null);
    setCurrentPage(1);
  };

  const clearPhoneValidationFilters = () => {
    setPhoneNumberFormat(null);
    setTimezoneDecision(null);
    setCurrentPage(1);
  };

  const phoneValidationFilterCount = [phoneNumberFormat, timezoneDecision]
    .filter(Boolean)
    .length;

  const phoneValidationPopoverContent = (
    <div style={{ width: 270 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>Phone Number Format</Text>
          <Select
            value={phoneNumberFormat}
            onChange={handlePhoneValidationFilterChange(setPhoneNumberFormat)}
            placeholder="All formats"
            allowClear
            style={{ width: '100%' }}
            options={[
              { value: 'valid', label: 'Valid Format' },
              { value: 'invalid', label: 'Invalid Format' },
              { value: 'not_checked', label: 'Not Checked' }
            ]}
          />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>Timezone Status</Text>
          <Select
            value={timezoneDecision}
            onChange={handlePhoneValidationFilterChange(setTimezoneDecision)}
            placeholder="All"
            allowClear
            style={{ width: '100%' }}
            options={[
              { value: 'normal', label: 'Match' },
              { value: 'mismatch', label: 'Mismatch' },
              { value: 'skipped', label: 'Skipped' },
              { value: 'not_available', label: 'Not available' }
            ]}
          />
        </div>
        <Button
          type="link"
          onClick={clearPhoneValidationFilters}
          disabled={phoneValidationFilterCount === 0}
          style={{ alignSelf: 'flex-end', padding: 0 }}
        >
          Clear filters
        </Button>
      </Space>
    </div>
  );

  const exportColumns = [
    { field: 'agentName', headerName: 'Agent' },
    { field: 'vehicleInfo.year', headerName: 'Year' },
    { field: 'vehicleInfo.make', headerName: 'Make' },
    { field: 'vehicleInfo.model', headerName: 'Model' },
    { field: 'personalInfo.fullName', headerName: 'Customer Name' },
    { field: 'personalInfo.email', headerName: 'Email' },
    { field: 'personalInfo.phoneNumber', headerName: 'Phone' },
    { field: 'leadStage', headerName: 'Status' },
    { field: 'createdAt', headerName: 'Created At' }
  ];

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh'
    }}>
      {/* Hero Header */}
      {/* Hero Header */}
      <div style={{ marginBottom: 2 }}>
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={16}>
            <Title level={2} style={{
              margin: 0,
              color: 'white',
              fontWeight: 700,
              textShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}>
              {loginAgent?.role === "ADMIN" ? "Lead Dashboard" : "My Leads"}
            </Title>
            <Text style={{
              color: 'rgba(255,255,255,0.9)',
              fontSize: 16,
              display: 'block',
              marginTop: 8
            }}>
             
            </Text>
          </Col>
         
        </Row>
      </div>

      <Card
        style={{
          borderRadius: 16,
          border: 'none',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          overflow: 'hidden',
          background: 'white'
        }}
      >

        {/* Main Controls */}
        <div style={{ padding: isSearching ? 0 : 20 }}>
          <Row gutter={[12, 12]} align="middle" wrap>
            {/* Search Input */}
            <Col flex="1 1 300px" style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <SearchFieldsSelect
                  value={searchFields}
                  onChange={handleSearchFieldsChange}
                  style={{ width: 130 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <EnhancedLeadSearch
                    value={searchTerm}
                    onChange={setSearchTerm}
                    isGlobalSearch={isSearching}
                    placeholder={getSearchFieldsPlaceholder(searchFields)}
                    loginAgent={loginAgent}
                    size="large"
                    style={{
                      borderRadius: 10,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      width: '100%'
                    }}
                  />
                </div>
              </div>
            </Col>

            {!isSearching && (
              <>
                {/* Filter Agent Dropdown */}
                {loginAgent?.role === "ADMIN" && (
                  <Col flex="1 1 140px" style={{ minWidth: 0 }}>
                    <Select
                      placeholder="Agent"
                      value={selectedAgentId}
                      onChange={setSelectedAgentId}
                      style={{ width: '100%' }}
                      size="middle"
                      allowClear
                      suffixIcon={<UserOutlined />}
                      dropdownMatchSelectWidth={false}
                    >
                      {agents.map(agent => (
                        <Select.Option key={agent._id} value={agent._id}>
                          <Space>
                            <Avatar size="small" icon={<UserOutlined />} />
                            <span>{agent.firstName} {agent.lastName}</span>
                          </Space>
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                )}
{/* Domain Filter Dropdown */}
{loginAgent?.role === "ADMIN" && !isSearching && (
  <Col flex="1 1 200px" style={{ minWidth: 0 }}>
    <Select
      placeholder="Filter by Domain"
      value={selectedDomain}
      onChange={setSelectedDomain}
      style={{ width: '100%' }}
      size="middle"
      allowClear
      suffixIcon={<FilterOutlined />}
      showSearch
      optionFilterProp="children"
      dropdownMatchSelectWidth={false}
      onClear={() => setSelectedDomain(null)}
    >
      {domains.map(domain => (
        <Select.Option key={domain} value={domain}>
          <Space>
            <GlobalOutlined style={{ color: '#1890ff' }} />
            <span style={{ fontSize: '13px' }}>{domain}</span>
          </Space>
        </Select.Option>
      ))}
    </Select>
  </Col>
)}
{/* Combined Stage, Assignment & Lead Type Dropdown */}
<Col flex="1 1 220px" style={{ minWidth: 0 }}>
  <Select
    placeholder="Filter by Stage, Status or Type"
    value={selectedLeadStage || selectedAssignment || selectedLeadType}
    onChange={(value) => {
      // Check if the selected value is from leadStages
      if (leadStages.includes(value)) {
        setSelectedLeadStage(value);
        setSelectedAssignment(null);
        setSelectedLeadType(null);
      } 
      // Check if it's from assignment options
      else if (['assigned', 'unassigned', 'queued'].includes(value)) {
        setSelectedAssignment(value);
        setSelectedLeadStage(null);
        setSelectedLeadType(null);
      }
      // Check if it's from lead type options
      else if (['inbound', 'outbound'].includes(value)) {
        setSelectedLeadType(value);
        setSelectedLeadStage(null);
        setSelectedAssignment(null);
      }
    }}
    style={{ width: '100%' }}
    size="middle"
    allowClear
    suffixIcon={<FilterOutlined />}
    dropdownMatchSelectWidth={false}
    onClear={() => {
      setSelectedLeadStage(null);
      setSelectedAssignment(null);
      setSelectedLeadType(null);
    }}
  >
    {/* Lead Stages Group */}
    <Select.OptGroup label="Lead Stages">
      {leadStages.map(stage => (
        <Select.Option key={stage} value={stage}>
          <Space>
            <Tag color={getStageColor(stage)} style={{ marginRight: 4, width: 12, height: 12, borderRadius: '50%', padding: 0 }} />
            <span>{stage}</span>
          </Space>
        </Select.Option>
      ))}
    </Select.OptGroup>

    {/* Assignment Status Group - Only for Admin */}
    {loginAgent?.role === "ADMIN" && (
      <Select.OptGroup label="Assignment Status">
        <Select.Option value="assigned">
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <span>Assigned</span>
          </Space>
        </Select.Option>
        <Select.Option value="unassigned">
          <Space>
            <ClockCircleOutlined style={{ color: '#faad14' }} />
            <span>Not Assigned</span>
          </Space>
        </Select.Option>
        <Select.Option value="queued">
          <Space>
            <ClockCircleOutlined style={{ color: '#1890ff' }} />
            <span>Queued</span>
          </Space>
        </Select.Option>
      </Select.OptGroup>
    )}

    {/* Lead Type Group */}
    <Select.OptGroup label="Lead Type">
      <Select.Option value="inbound">
        <Space>
          <Tag color="green" style={{ marginRight: 4, width: 8, height: 8, borderRadius: '50%', padding: 0 }} />
          <span>Inbound</span>
        </Space>
      </Select.Option>
      <Select.Option value="outbound">
        <Space>
          <Tag color="blue" style={{ marginRight: 4, width: 8, height: 8, borderRadius: '50%', padding: 0 }} />
          <span>Outbound</span>
        </Space>
      </Select.Option>
    </Select.OptGroup>
  </Select>
</Col>
                

                {loginAgent?.role === "ADMIN" && (
                  <Col flex="1 1 170px" style={{ minWidth: 0 }}>
                    <Popover
                      content={phoneValidationPopoverContent}
                      title="Lead Validation"
                      trigger="click"
                      placement="bottomLeft"
                    >
                      <div style={{ position: 'relative', width: '100%' }}>
                        <Button
                          icon={<PhoneOutlined />}
                          type={phoneValidationFilterCount ? 'primary' : 'default'}
                          style={{ width: '100%', paddingRight: phoneValidationFilterCount ? 42 : undefined }}
                        >
                          Lead Validation{phoneValidationFilterCount ? ` (${phoneValidationFilterCount})` : ''}
                        </Button>
                        {phoneValidationFilterCount > 0 && (
                          <Tooltip title="Clear phone validation filters">
                            <Button
                              type="text"
                              size="small"
                              icon={<CloseOutlined />}
                              aria-label="Clear phone validation filters"
                              onMouseDown={(event) => event.stopPropagation()}
                              onClick={(event) => {
                                event.stopPropagation();
                                clearPhoneValidationFilters();
                              }}
                              style={{
                                position: 'absolute',
                                top: '50%',
                                right: 6,
                                transform: 'translateY(-50%)',
                                color: '#fff'
                              }}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </Popover>
                  </Col>
                )}

                {/* Date basis - Admin only */}
                {loginAgent.role === "ADMIN" && (
                  <Col
                    xs={{ flex: '1 1 100%' }}
                    sm={{ flex: '0 1 170px' }}
                    style={{ minWidth: 0, maxWidth: '100%' }}
                  >
                    <Select
                      value={dateBasis}
                      onChange={(value) => {
                        setDateBasis(value);
                        setCurrentPage(1);
                      }}
                      style={{ width: '100%' }}
                      size="middle"
                      options={[
                        { value: 'default', label: 'Assigned Date' },
                        { value: 'createdAt', label: 'Created Date' },
                      ]}
                    />
                  </Col>
                )}

                {/* Date Range Picker - Compact Version */}
                <Col
                  xs={{ flex: '1 1 100%' }}
                  sm={{ flex: '0 1 300px' }}
                  style={{ minWidth: 0, maxWidth: '100%' }}
                >
                  <RangePicker
                    value={dateRange}
                    onChange={(dates) => {
                      if (dates && dates[0] && dates[1]) {
                        setDateRange(dates);
                      } else {
                        setDateRange(getTodayDateRange());
                      }
                    }}
                    presets={datePresets}
                    disabledDate={(current) => current && current > dayjs().endOf("day")}
                    style={{ width: '100%' }}
                    size="middle"
                    suffixIcon={<CalendarOutlined />}
                    format="MM/DD/YYYY"
                    allowClear
                    onClear={() => setDateRange(getTodayDateRange())}
                    placeholder={['Start', 'End']}
                  />
                </Col>

              </>
            )}

            {/* Action Buttons - With Text */}
            <Col flex="1 1 240px" style={{ minWidth: 0 }}>
              <Space size="small" wrap style={{ width: '100%' }}>
                <Button
                  icon={<SyncOutlined />}
                  onClick={fetchLeads}
                  size="middle"
                  loading={loading}
                  style={{ borderRadius: 8, padding: '4px 8px' }}
                >
                  Refresh
                </Button>

                {loginAgent.role === "ADMIN" && (
                  <>
                    {selectedRowKeys.length > 0 && (
                      <Popconfirm
                        title="Delete selected records?"
                        description="Are you sure you want to delete these records?"
                        onConfirm={deleteSelected}
                        okText="Yes"
                        cancelText="No"
                        placement="bottomRight"
                      >
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          size="middle"
                          style={{ borderRadius: 8, padding: '4px 8px' }}
                        >
                          Delete
                        </Button>
                      </Popconfirm>
                    )}

                    <Button
                      icon={<DeleteFilled />}
                      onClick={() => setRecycleBinVisible(true)}
                      size="middle"
                      style={{ borderRadius: 8, padding: '4px 8px' }}
                    >
                      Bin
                    </Button>

  {/* Lead export is super admin only (backend also blocks it) */}
  {isSuperAdminUser() && (
  <LeadDownload
  columns={exportColumns}
  filters={{
    startDate: dateRange[0]?.format('YYYY-MM-DD'),
    endDate: dateRange[1]?.format('YYYY-MM-DD'),
    status: selectedLeadStage,
    agentId: selectedAgentId,
    type: selectedLeadType,
    leadStatus: selectedAssignment,
    source: selectedDomain,
    tierType: activeView.startsWith("TIER-") ? activeView : null,
    tierLane: activeView === "TIER-1" && selectedTierLane !== 'all' ? selectedTierLane : null
  }}
  dateRange={dateRange}
  buttonProps={{
    size: 'middle',
    children: 'Export',
    style: { borderRadius: 8, padding: '4px 8px' }
  }}
/>
  )}
                    {isSearching && <Button
                      type="primary"
                      ghost
                      icon={<ClearOutlined />}
                      onClick={clearAllFilters}
                      size="middle"
                      style={{
                        borderRadius: 8, padding: '2px 8px'
                      }}
                    >
                      Clear Search
                    </Button>}
                  </>
                )}

              
              </Space>
            </Col>
          </Row>
        </div>


        {/* View Controls */}
{!isSearching && (
  <div style={{ padding: '0 20px 20px' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        {/* Left side - Admin tabs */}
        <Tabs
          activeKey={activeView}
          onChange={setActiveView}
          size="large"
          tabBarStyle={{ margin: 0 }}
          items={[
            // Admin only tabs
            ...(loginAgent?.role === "ADMIN" ? [
              {
                key: 'all',
                label: <span><AppstoreOutlined /> All Data</span>
              },
              {
                key: 'TIER-1',
                label: (
                  <span>
                    <TrophyOutlined style={{ color: '#ffd700' }} />
                    <Text style={{ color: '#ffd700', fontWeight: 600 }}>TIER-1</Text>
                  </span>
                )
              },
              {
                key: 'TIER-2',
                label: (
                  <span>
                    <StarOutlined style={{ color: '#c0c0c0' }} />
                    <Text style={{ color: '#c0c0c0', fontWeight: 600 }}>TIER-2</Text>
                  </span>
                )
              },
              {
                key: 'TIER-3',
                label: (
                  <span>
                    <TagOutlined style={{ color: '#cd7f32' }} />
                    <Text style={{ color: '#cd7f32', fontWeight: 600 }}>TIER-3</Text>
                  </span>
                )
              },
              {
                key: 'unassigned',
                label: (
                  <span>
                    <ClockCircleOutlined style={{ color: '#faad14' }} />
                    <Text style={{ color: '#faad14', fontWeight: 600 }}>Not Assigned</Text>
                  </span>
                )
              },
    
            ] : [])
          ]}
        />

        {loginAgent?.role === "ADMIN" && activeView === "TIER-1" && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 18,
              flexWrap: 'wrap',
              marginTop: 6,
              padding: '6px 0 2px 12px',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            {[
              { value: 'all', label: 'All Tier-1' },
              ...groupsForTier('TIER-1').map((group) => ({
                value: group.key,
                label: group.name
              }))
            ].map((lane) => {
              const isActive = selectedTierLane === lane.value;

              return (
                <Button
                  key={lane.value}
                  type="text"
                  onClick={() => setSelectedTierLane(lane.value)}
                  style={{
                    padding: '8px 0 7px',
                    borderRadius: 0,
                    color: isActive ? '#1677ff' : '#262626',
                    fontWeight: isActive ? 700 : 600,
                    borderBottom: isActive ? '2px solid #1677ff' : '2px solid transparent',
                    boxShadow: 'none',
                    lineHeight: 1.2
                  }}
                >
                  {lane.label}
                </Button>
              );
            })}
          </div>
        )}
      </div>

      <Space size="middle">
        {loginAgent?.role !== "SERVICE-AGENT" && (
          <AddLead refreshLeads={fetchLeads} buttonProps={{
            size: 'middle',
            icon: <PlusOutlined />,
            children: 'Add Lead',
            style: { borderRadius: 8, padding: '4px 8px' }
          }} />
        )}
      </Space>
    </div>
  </div>
)}
        {/* Results Header */}
        <div style={{
          padding: '20px',
          marginTop: "10px",
          background: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          borderBottom: '1px solid #e9ecef'
        }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Text strong style={{ fontSize: 16 }}>
                  {isSearching ? (
                    <>
                      <DatabaseOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                      Search Results
                    </>
                  ) : (
                    <>
                      <CalendarOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                      Showing leads from{' '}
                      {dateRange && dateRange[0] && dateRange[1] ? (
                        dateRange[0].isSame(dateRange[1], 'day') ? (
                          // Single day selected
                          dateRange[0].format('MM/DD/YYYY')
                        ) : dateRange[0].isSame(dayjs().startOf('day')) &&
                          dateRange[1].isSame(dayjs().endOf('day')) ? (
                          // Today
                          "today"
                        ) : dateRange[0].isSame(dayjs().subtract(1, 'day').startOf('day')) &&
                          dateRange[1].isSame(dayjs().subtract(1, 'day').endOf('day')) ? (
                          // Yesterday
                          "yesterday"
                        ) : (
                          // Normal range
                          <>
                            {dateRange[0].format('MMM D')}
                            {' → '}
                            {dateRange[1].format('MM/DD/YYYY')}
                          </>
                        )
                      ) : (
                        "the selected period"
                      )}
                    </>
                  )}
                </Text>
                <Badge
                  count={displayedLeads.length}
                  showZero
                  style={{
                    backgroundColor: '#52c41a',
                    fontSize: 12,
                    fontWeight: 'bold'
                  }}
                />
                <Text type="secondary">
                  of {totalRecords.toLocaleString()} total records
                </Text>
                {!isSearching && (
                  <Text type="secondary">
                    | Date: {dateRange[0]?.format('MM/DD/YYYY')}
                  </Text>
                )}
              </Space>
            </Col>
            <Col>
              <Space>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={totalRecords}
                  showSizeChanger
                  pageSizeOptions={["12", "24", "36", "48", "100"]}
                  onChange={(page, size) => handleTableChange({ current: page, pageSize: size })}
                  size="small"
                  simple
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
                />
              </Space>
            </Col>
          </Row>
        </div>

        {/* Leads Content */}
        <div style={{ padding: 20 }}>
          {loading && displayedLeads.length === 0 ? (
            <div style={{
              textAlign: "center",
              padding: "80px 0",
              background: 'white',
              borderRadius: 12
            }}>
              <Spin
                size="large"
                tip={
                  <Text style={{
                    color: '#667eea',
                    fontSize: 16,
                    marginTop: 16
                  }}>
                    Loading leads...
                  </Text>
                }
                style={{ fontSize: 48 }}
              />
            </div>
          ) : displayedLeads.length === 0 ? (
            <Empty
              imageStyle={{ height: 120 }}
              description={
                <Space direction="vertical" size={8}>
                  <Text strong style={{ fontSize: 18 }}>
                    {isSearching ? "No matching results" : "No leads for today"}
                  </Text>
                  <Text type="secondary">
                    {isSearching
                      ? "Try adjusting your search terms or filters"
                      : "No leads have been created or assigned today"}
                  </Text>
                  {!isSearching && (
                    <Button
                      type="primary"
                      icon={<CalendarOutlined />}
                      onClick={() => setDateRange(getTodayDateRange())}
                      size="middle"
                    >
                      Refresh Today's Data
                    </Button>
                  )}
                </Space>
              }
              style={{
                padding: '80px 0',
                background: 'white',
                borderRadius: 12
              }}
            />
          ) : (
            <>
              {loading && (
                // Stays in view wherever the list is scrolled.
                <div style={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 999,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 18px',
                  background: 'white',
                  borderRadius: 24,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                }}>
                  <Spin />
                  <Text style={{ color: '#667eea' }}>Loading leads...</Text>
                </div>
              )}
              {/* While the next results load, the current cards stay on screen,
                  dimmed and out of reach: no click, focus or screen reader. */}
              <div
                ref={(node) => { if (node) node.inert = loading; }}
                aria-busy={loading}
                style={{
                  opacity: loading ? 0.5 : 1,
                  pointerEvents: loading ? 'none' : undefined,
                  transition: 'opacity 0.2s'
                }}
              >
                <Row gutter={[16, 16]} key={resultsVersion}>
                  {displayedLeads.map((record) => (
                    <Col span={24} key={record._id}>
                      <LeadCard
                        record={record}
                        navigate={navigate}
                        loginAgent={loginAgent}
                        onDelete={handleCardDelete}
                        selectedRowKeys={selectedRowKeys}
                        setSelectedRowKeys={setSelectedRowKeys}
                        isGlobalSearchResult={isSearching}
                        onRefresh={handleCardRefresh}
                        onBeforeNavigateToLeadDetails={handleCardBeforeNavigate}
                      />
                    </Col>
                  ))}
                </Row>
              </div>
            </>
          )}

          {/* Bottom Pagination */}
          {displayedLeads.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 32,
              padding: 20,
              background: '#f8f9fa',
              borderRadius: 12
            }}>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalRecords}
                showSizeChanger
                pageSizeOptions={["12", "24", "36", "48", "100"]}
                onChange={(page, size) => handleTableChange({ current: page, pageSize: size })}
                showQuickJumper
                showTotal={(total, range) =>
                  `Showing ${range[0]}-${range[1]} of ${total} leads`
                }
                size="default"
              />
            </div>
          )}
        </div>
      </Card>

      <RecycleBin
        visible={recycleBinVisible}
        onClose={() => setRecycleBinVisible(false)}
        onRestoreSuccess={fetchLeads}
      />
    </div>
  );
};

export default MyLeads;
// 
